// The kc_action result is handed across the full-page OIDC redirect (in-memory
// state doesn't survive the navigation). It's modeled as two one-shot cells in
// sessionStorage: `pending` (recorded before leaving) and `success` (set on
// return only when Keycloak reports kc_action_status=success — not on "Back").

const PENDING_KEY = "kc_pending_action"
const SUCCESS_KEY = "kc_action_success"

/** Access sessionStorage without letting a blocked-storage SecurityError escape. */
export const safeSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** A single value handed across a full-page redirect: write once, read-and-clear once. */
class StoredAction {
  constructor(
    private readonly key: string,
    private readonly storage: Storage | null
  ) {}

  put(value: string): void {
    try {
      this.storage?.setItem(this.key, value)
    } catch {
      // Storage blocked — the toast is optional, so carry on.
    }
  }

  take(): string | null {
    try {
      const value = this.storage?.getItem(this.key) ?? null
      this.storage?.removeItem(this.key)
      return value
    } catch {
      return null
    }
  }
}

/**
 * The kc_action handoff, as two one-shot cells. `capture` reconciles them: it always clears pending,
 * and forwards it to success only when Keycloak confirms, so Back/cancel/error leaves nothing to toast.
 */
export const kcActionResult = (
  storage: Storage | null = safeSessionStorage()
) => {
  const pending = new StoredAction(PENDING_KEY, storage)
  const success = new StoredAction(SUCCESS_KEY, storage)

  return {
    /** Before redirecting to Keycloak, record which action was initiated. */
    markPending: (action: string): void => pending.put(action),

    /**
     * Called once at app bootstrap (before OIDC processes the callback): clear
     * the pending action and, only on kc_action_status=success, forward it to
     * the success cell for the hub to consume.
     */
    capture: (search: string): void => {
      const action = pending.take()
      const status = new URLSearchParams(search).get("kc_action_status")
      if (status === "success" && action) success.put(action)
    },

    /**
     * Read and clear the completed action (null when none). Clearing on read
     * makes the toast fire at most once (refresh / StrictMode re-mount safe).
     */
    consumeSuccess: (): string | null => success.take(),
  }
}
